import { Big } from "big.js";
import { LedgerEntry, recoveredOf } from "./Analytics.ts";
import type { Case } from "./CaseModel.ts";

/**
 * What a claim costs a party, by the heads of an insurer's reserve, each as
 * a cost (positive): the indemnity (damages it pays and settlement sums it
 * pays), the claimant's costs it pays, its own defence costs, and less what it
 * recovers (negative: damages and costs awarded to it, settlement sums it
 * receives, net of what enforcement falls short of). Together they are the
 * party's net result with the sign turned.
 */
export type ReserveHeads = {
  readonly indemnity: number;
  readonly claimantCosts: number;
  readonly defenceCosts: number;
  readonly recoveries: number;
};

export const RESERVE_HEADS = [
  "indemnity",
  "claimantCosts",
  "defenceCosts",
  "recoveries",
] as const satisfies ReadonlyArray<keyof ReserveHeads>;

type Sums = { -readonly [head in keyof ReserveHeads]: Big };

const zero = (): Sums => ({
  indemnity: new Big(0),
  claimantCosts: new Big(0),
  defenceCosts: new Big(0),
  recoveries: new Big(0),
});

// a head that comes to nothing is 0, never -0, which would read "-£0.00"
const asHeads = (sums: Sums): ReserveHeads => ({
  indemnity: sums.indemnity.toNumber() || 0,
  claimantCosts: sums.claimantCosts.toNumber() || 0,
  defenceCosts: sums.defenceCosts.toNumber() || 0,
  recoveries: sums.recoveries.toNumber() || 0,
});

/**
 * The heads of an outcome's ledger for the party it is of: every entry that
 * counts in the party's net result (see signedAmountFor), in the head it
 * belongs to. With `paidOnly`, only what is spent whatever the outcome: its
 * incurred costs and its accepted settlements.
 */
export function reserveHeadsOf(
  entries: ReadonlyArray<LedgerEntry>,
  { paidOnly = false }: { paidOnly?: boolean } = {},
): ReserveHeads {
  const sums = zero();
  const add = (head: keyof ReserveHeads, cost: number) => {
    sums[head] = sums[head].add(cost);
  };
  for (const entry of entries) {
    switch (entry.kind) {
      case "cost":
        if (entry.entity !== "party") break;
        if (paidOnly && entry.estimated) break;
        add("defenceCosts", -(entry.amount + (entry.vat ?? 0)));
        if (!paidOnly) add("recoveries", -recoveredOf(entry.recovered));
        break;
      case "agreementFee":
        if (paidOnly || entry.payer !== "party") break;
        add("defenceCosts", entry.amount + (entry.vat ?? 0));
        add("recoveries", -recoveredOf(entry.recovered));
        break;
      case "enforcementCost":
        if (paidOnly) break;
        add("defenceCosts", -(entry.amount + (entry.vat ?? 0)));
        add("recoveries", -recoveredOf(entry.recovered));
        break;
      case "settlement":
        add(entry.amount < 0 ? "indemnity" : "recoveries", -entry.amount);
        break;
      case "quantumToPay":
        if (!paidOnly) add("indemnity", -entry.amount);
        break;
      case "recoveryToPay":
        if (!paidOnly) add("claimantCosts", -entry.amount);
        break;
      case "quantumAwarded":
      case "enforcementShortfall":
        if (!paidOnly) add("recoveries", -entry.amount);
        break;
    }
  }
  return asHeads(sums);
}

/** The heads added up: the party's net result with the sign turned. */
export const reserveTotal = (heads: ReserveHeads): number =>
  RESERVE_HEADS.reduce(
    (sum, head) => sum.add(heads[head]),
    new Big(0),
  ).toNumber();

/**
 * The reserve a party's outcomes indicate: each head weighed by the chance of
 * its outcome. Undefined without outcomes, or where one has no reserve (on
 * results stored before reserves were).
 */
export function meanReserve(
  outcomes: ReadonlyArray<{ probability: number; reserve?: ReserveHeads }>,
): ReserveHeads | undefined {
  if (outcomes.length === 0) return undefined;
  const sums = zero();
  for (const { probability, reserve } of outcomes) {
    if (reserve === undefined) return undefined;
    for (const head of RESERVE_HEADS)
      sums[head] = sums[head].add(new Big(reserve[head]).mul(probability));
  }
  return asHeads(sums);
}

/** What is still to be paid of each head. */
export function outstandingOf(
  total: ReserveHeads,
  paid: ReserveHeads,
): ReserveHeads {
  const sums = zero();
  for (const head of RESERVE_HEADS)
    sums[head] = new Big(total[head]).sub(paid[head]);
  return asHeads(sums);
}

/** An outcome as the reserve views read it: its chance, its heads and the heads of claim it loses. */
export type ReserveOutcome = {
  readonly probability: number;
  readonly reserve?: ReserveHeads;
  readonly issuesWon: ReadonlyArray<string>;
  readonly issuesLost: ReadonlyArray<string>;
};

/**
 * The ultimate cost at each chance: the least cost that the outcomes up to it,
 * cheapest first, reach with that much of their probability, 0.9 for P90.
 * The maximum is the cost of the dearest outcome that can happen.
 */
export function percentiles(
  samples: ReadonlyArray<{ probability: number; value: number }>,
  chances: ReadonlyArray<number>,
): { percentiles: number[]; maximum: number } | undefined {
  const sorted = samples
    .filter(({ probability }) => probability > 0)
    .sort((a, b) => a.value - b.value);
  if (sorted.length === 0) return undefined;
  const total = sorted.reduce((sum, { probability }) => sum + probability, 0);
  const at = (chance: number) => {
    let cumulative = 0;
    for (const { probability, value } of sorted) {
      cumulative += probability / total;
      // a little slack for chances that add up to a hair below the mark
      if (cumulative >= chance - 1e-9) return value;
    }
    return sorted[sorted.length - 1].value;
  };
  return {
    percentiles: chances.map(at),
    maximum: sorted[sorted.length - 1].value,
  };
}

/** A group of outcomes: its chance, and its heads, on average and weighed by that chance. */
export type ReserveScenario<Key> = {
  readonly key: Key;
  readonly probability: number;
  // the heads were the claim to end in this scenario
  readonly mean: ReserveHeads;
  // its part of the reserve: the heads weighed by the chance of the scenario
  readonly weighted: ReserveHeads;
};

function scenarios<Key>(
  outcomes: ReadonlyArray<ReserveOutcome>,
  keys: ReadonlyArray<Key>,
  keyOf: (outcome: ReserveOutcome & { reserve: ReserveHeads }) => Key,
): ReserveScenario<Key>[] | undefined {
  if (outcomes.some(({ reserve }) => reserve === undefined)) return undefined;
  return keys.flatMap((key) => {
    const of = outcomes.filter(
      (outcome) =>
        outcome.probability > 0 &&
        keyOf(outcome as ReserveOutcome & { reserve: ReserveHeads }) === key,
    );
    const probability = of.reduce(
      (sum, outcome) => sum.add(outcome.probability),
      new Big(0),
    );
    if (probability.eq(0)) return [];
    const weighted = meanReserve(of)!;
    const mean = zero();
    for (const head of RESERVE_HEADS)
      mean[head] = new Big(weighted[head]).div(probability);
    return [
      {
        key,
        probability: probability.toNumber(),
        mean: asHeads(mean),
        weighted,
      },
    ];
  });
}

/**
 * The id a claim against several defendants is decided under against one of
 * them: the calculation copies it per defendant (see idAgainst).
 */
export const copyIdAgainst = (id: string, defendantId: string): string =>
  `${id}_v_${defendantId}`;

/**
 * The ids the heads of claim of a party are decided under: its claims, by it
 * or against it, each against the defendant it is decided against.
 */
export function decidedClaimIdsOf(c: Case, partyId: string): string[] {
  return (c.claims ?? []).flatMap(({ id, claimant, defendants = [] }) => {
    const against =
      claimant.id === partyId
        ? defendants.map((defendant) => defendant.id)
        : defendants.some((defendant) => defendant.id === partyId)
          ? [partyId]
          : [];
    return defendants.length < 2
      ? against.length > 0
        ? [id]
        : []
      : against.map((defendantId) => copyIdAgainst(id, defendantId));
  });
}

/** How many of the heads of claim the party loses in an outcome. */
export const HEADS_LOST = ["none", "some", "all"] as const;
export type HeadsLost = (typeof HEADS_LOST)[number];

/**
 * The outcomes by how many of the heads of claim the party loses of those
 * decided in them: none (its defence succeeds), some, or all. `claimIds` are
 * the claims of the party, by or against it; a claim settled before the trial
 * is decided in no outcome. Undefined where an outcome has no reserve.
 */
export function scenariosByHeads(
  outcomes: ReadonlyArray<ReserveOutcome>,
  claimIds: ReadonlyArray<string>,
): ReserveScenario<HeadsLost>[] | undefined {
  const claims = new Set(claimIds);
  return scenarios(outcomes, HEADS_LOST, ({ issuesWon, issuesLost }) => {
    const lost = issuesLost.filter((id) => claims.has(id)).length;
    const won = issuesWon.filter((id) => claims.has(id)).length;
    return lost === 0 ? "none" : won === 0 ? "all" : "some";
  });
}

/** How many bands of the ultimate cost the outcomes are grouped in. */
export const COST_BANDS = 4;

/**
 * The outcomes by their ultimate cost, in equal bands from the cheapest to the
 * dearest: band 0 the cheapest, each with the costs it spans. Undefined where
 * an outcome has no reserve.
 */
export function scenariosByCost(
  outcomes: ReadonlyArray<ReserveOutcome>,
): (ReserveScenario<number> & { from: number; to: number })[] | undefined {
  const costs = outcomes
    .filter(({ probability }) => probability > 0)
    .map(({ reserve }) => reserve && reserveTotal(reserve));
  if (costs.some((cost) => cost === undefined)) return undefined;
  if (costs.length === 0) return [];
  const min = Math.min(...(costs as number[]));
  const max = Math.max(...(costs as number[]));
  const width = (max - min) / COST_BANDS;
  const bandOf = (cost: number) =>
    width === 0
      ? 0
      : Math.min(Math.floor((cost - min) / width), COST_BANDS - 1);
  const bands = Array.from({ length: COST_BANDS }, (_, band) => band);
  return scenarios(outcomes, bands, ({ reserve }) =>
    bandOf(reserveTotal(reserve)),
  )?.map((scenario) => ({
    ...scenario,
    from: min + scenario.key * width,
    to:
      scenario.key === COST_BANDS - 1 ? max : min + (scenario.key + 1) * width,
  }));
}

/** The heads of several added up, head by head. */
export function sumHeads(list: ReadonlyArray<ReserveHeads>): ReserveHeads {
  const sums = zero();
  for (const heads of list)
    for (const head of RESERVE_HEADS) sums[head] = sums[head].add(heads[head]);
  return asHeads(sums);
}

/** A party's reserve as its outcomes indicate it: in all, and what is paid of it. */
export type PartyReserve = { total: ReserveHeads; paid: ReserveHeads };

/**
 * The reserve of a party from the results of its case; undefined until
 * results with reserves are there (they come with the next calculation).
 */
export function partyReserveOf(
  results: Pick<Case, "results">["results"],
  partyId: string,
): PartyReserve | undefined {
  const outcomes = results?.outcomes.find((party) => party.partyId === partyId);
  const total = outcomes && meanReserve(outcomes.outcomes);
  return total && outcomes.reservePaid
    ? { total, paid: outcomes.reservePaid }
    : undefined;
}
